import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { playBell, startBellLoop, stopBellLoop } from "@/lib/sound";
import { formatDateTime } from "@/lib/format";
import { vehicleInclusions } from "@/lib/inclusions";
import RouteMap from "@/components/RouteMap";
import {
  Bell, LogOut, ArrowRight, Clock, CheckCircle2, Hourglass,
  ShieldX, Car, IndianRupee, Loader2, PhoneCall, Truck, Inbox,
  FileText, Upload, FileCheck2, AlertCircle, XCircle, X, User, Handshake, Wallet, ShoppingCart, Plus, Check, Star,
} from "lucide-react";

export default function DriverDashboard() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [profile, setProfile] = useState({ phone: user?.phone || "", vehicle_type: user?.vehicle_type || "", cities: user?.cities?.length ? user.cities : (user?.city ? [user.city] : []) });
  const [cityInput, setCityInput] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploading, setUploading] = useState({});
  const [accepting, setAccepting] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [counterFor, setCounterFor] = useState(null);
  const [counterAmount, setCounterAmount] = useState("");
  const [counterBusy, setCounterBusy] = useState(false);
  const [wallet, setWallet] = useState(0);
  const [topupAmt, setTopupAmt] = useState("500");
  const [topupBusy, setTopupBusy] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [ringing, setRinging] = useState(false);
  const lastBroadcastRef = useRef(null);
  const soundEnabledRef = useRef(false);
  const [pushState, setPushState] = useState("checking"); // checking | default | enabled | denied | unsupported
  const [online, setOnline] = useState(!!user?.is_online);
  const [onlineBusy, setOnlineBusy] = useState(false);
  const getPos = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("unsupported"));
    navigator.geolocation.getCurrentPosition((p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }), reject, { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 });
  });
  const toggleOnline = async () => {
    setOnlineBusy(true);
    try {
      let pos = {};
      if (!online) {
        try { pos = await getPos(); } catch (e) {
          toast.error("Allow location access to go online — Admin uses it to match you with nearby trips.");
          return;
        }
      }
      const r = await api.put("/driver/online", { online: !online, ...pos });
      setOnline(r.data.online);
      toast.success(r.data.online ? "You're online — Admin can see you" : "You're offline");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change status"); }
    finally { setOnlineBusy(false); }
  };
  useEffect(() => {
    if (!online) return;
    const send = () => getPos()
      .then((p) => api.put("/driver/location", p))
      .catch(() => { api.put("/driver/online", { online: false }).catch(() => {}); setOnline(false); toast.error("Location turned off — you are now offline"); });
    send();
    const t = setInterval(send, 60000);
    return () => { clearInterval(t); };
  }, [online]);

  useEffect(() => {
    (async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
        return setPushState("unsupported");
      }
      if (Notification.permission === "denied") return setPushState("denied");
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushState(sub ? "enabled" : "default");
      } catch (e) { setPushState("default"); }
    })();
  }, []);

  const urlB64ToUint8Array = (b64) => {
    const padding = "=".repeat((4 - (b64.length % 4)) % 4);
    const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  };

  const enablePush = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setPushState("denied");
        return toast.error("Notifications are blocked — enable them in browser settings");
      }
      const reg = await navigator.serviceWorker.ready;
      const keyRes = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(keyRes.data.publicKey) });
      await api.post("/driver/push/subscribe", { subscription: sub.toJSON() });
      setPushState("enabled");
      toast.success("Push alerts enabled — you'll get lead alerts even when the app is closed");
    } catch (e) {
      const reason = e?.response?.data?.detail || e?.message || "unknown error";
      console.error("Push enable failed:", e);
      toast.error(`Could not enable push alerts: ${reason}`);
    }
  };
  const rcRef = useRef(null);
  const dlRef = useRef(null);

  const approved = user?.driver_status === "approved";

  useEffect(() => {
    api.get("/vehicle-types").then((r) => {
      setVehicleTypes(r.data);
      setProfile((p) => ({ ...p, vehicle_type: p.vehicle_type || r.data[0]?.name || "" }));
    }).catch(() => {});
  }, []);

  const loadLeads = useCallback(async () => {
    if (!approved) return;
    try {
      const res = await api.get("/driver/leads");
      const data = res.data;
      const latest = data[0]?.broadcast_at || null;
      if (lastBroadcastRef.current !== null && latest && latest !== lastBroadcastRef.current) {
        setRinging(true);
        startBellLoop();
        toast.success("New lead broadcasted!");
      }
      if (latest) lastBroadcastRef.current = latest;
      else if (lastBroadcastRef.current === null) lastBroadcastRef.current = "";
      setLeads(data);
    } catch (e) {}
  }, [approved]);

  useEffect(() => {
    loadLeads();
    const t = setInterval(loadLeads, 5000);
    return () => { clearInterval(t); stopBellLoop(); };
  }, [loadLeads]);

  const stopRing = () => {
    setRinging(false);
    stopBellLoop();
  };

  const enableSound = () => {
    soundEnabledRef.current = true;
    playBell();
    toast.success("Sound alerts enabled");
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const saveProfile = async () => {
    if (!profile.phone) return toast.error("Enter your phone number");
    if (!profile.vehicle_type) return toast.error("Select your vehicle type");
    if (!profile.cities.length) return toast.error("Add at least one operating city");
    setSavingProfile(true);
    try {
      const res = await api.put("/driver/profile", profile);
      setUser(res.data);
      toast.success("Profile saved");
    } catch (e) {
      toast.error("Could not save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const uploadDoc = async (docType, fileObj) => {
    if (!fileObj) return;
    setUploading({ ...uploading, [docType]: true });
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", fileObj);
      const res = await api.post("/driver/documents", fd);
      setUser(res.data);
      toast.success(`${docType.toUpperCase()} uploaded successfully`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading({ ...uploading, [docType]: false });
    }
  };

  const acceptLead = async (id) => {
    setAccepting(id);
    stopRing();
    try {
      const res = await api.post(`/driver/leads/${id}/accept`);
      toast.success(`Lead accepted! Customer: +91 ${res.data.customer_phone}`);
      await loadLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept");
      await loadLeads();
    } finally {
      setAccepting(null);
    }
  };

  const rejectLead = async (id) => {
    setRejecting(id);
    stopRing();
    try {
      await api.post(`/driver/leads/${id}/reject`);
      toast.success("Lead rejected — removed from your list");
      await loadLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject");
      await loadLeads();
    } finally {
      setRejecting(null);
    }
  };

  const counterLead = async (id) => {
    if (!counterAmount || Number(counterAmount) <= 0) return toast.error("Enter a valid counter fare");
    setCounterBusy(true);
    try {
      await api.post(`/driver/leads/${id}/counter`, { amount: Number(counterAmount) });
      toast.success("Counter offer sent to admin");
      setCounterFor(null);
      setCounterAmount("");
      await loadLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send counter");
    } finally {
      setCounterBusy(false);
    }
  };

  const loadWallet = async () => {
    try { const r = await api.get("/wallet"); setWallet(r.data.balance); } catch (e) {}
  };
  useEffect(() => { if (approved) loadWallet(); }, [approved]);

  const topUp = async () => {
    const amt = Number(topupAmt);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setTopupBusy(true);
    try {
      const res = await api.post("/wallet/topup", { amount: amt });
      if (res.data.simulated) {
        setWallet(res.data.balance);
        toast.success(`₹${amt} added to wallet`);
      } else if (window.Razorpay) {
        const opts = {
          key: res.data.key_id, amount: res.data.amount, currency: "INR", order_id: res.data.order_id,
          name: "RouteWheels Wallet", description: "Wallet top-up",
          handler: async (r) => {
            try {
              const v = await api.post("/wallet/topup/verify", {
                razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id,
                razorpay_signature: r.razorpay_signature,
              });
              setWallet(v.data.balance);
              toast.success("Wallet topped up!");
            } catch (e) { toast.error("Payment verification failed"); }
          },
          prefill: { name: user.name, email: user.email || "", contact: user.phone || "" },
          theme: { color: "#f59e0b" },
        };
        new window.Razorpay(opts).open();
      } else {
        toast.error("Payment library not loaded");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Top-up failed");
    } finally {
      setTopupBusy(false);
    }
  };

  const purchaseLead = async (id) => {
    setPurchasing(id);
    stopRing();
    try {
      await api.post(`/driver/leads/${id}/purchase`);
      toast.success("Lead purchased! Fares unlocked — you can now bid.");
      await loadWallet();
      await loadLeads();
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Insufficient balance — top up your wallet");
      else toast.error(e?.response?.data?.detail || "Purchase failed");
    } finally {
      setPurchasing(null);
    }
  };

  const regComplete = user?.registration_complete;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="driver-pwa-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-slate-900/95 text-white">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
              <Truck className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <p className="font-display font-bold leading-tight">Driver Hub</p>
              <p className="text-[11px] text-slate-300 -mt-0.5 truncate max-w-[140px]">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {user?.driver_status === "approved" && (
              <button data-testid="driver-online-toggle" onClick={toggleOnline} disabled={onlineBusy}
                className={`h-9 px-3 rounded-full text-xs font-bold flex items-center gap-1.5 transition-colors ${online ? "bg-emerald-500 text-slate-900" : "bg-white/10 text-slate-300 hover:bg-white/20"}`}>
                <span className={`w-2 h-2 rounded-full ${online ? "bg-white" : "bg-slate-400"}`} />
                <span data-testid="driver-online-status">{online ? "Online" : "Offline"}</span>
              </button>
            )}
            <button
              data-testid="driver-notification-bell-button"
              onClick={() => { enableSound(); stopRing(); }}
              className="relative w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
              title="Enable sound alerts / open notifications"
            >
              {ringing && <span data-testid="driver-ringing-indicator" className="absolute inset-0 rounded-full bg-amber-500/40 animate-ping" />}
              <Bell className={`w-5 h-5 text-amber-400 ${ringing ? "animate-bell" : ""}`} />
              {leads.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-amber-500 text-slate-900 text-[11px] font-bold flex items-center justify-center">
                  {leads.length}
                </span>
              )}
            </button>
            <button
              data-testid="driver-logout-button"
              onClick={doLogout}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-5 pb-24">
        <StatusBanner status={user?.driver_status} regComplete={regComplete} />

        {/* Profile */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
          <h2 className="font-display font-bold text-slate-900 flex items-center gap-2">
            <Car className="w-5 h-5 text-amber-500" /> Driver Profile
          </h2>
          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-sm">Phone Number</Label>
              <div className="mt-1.5 flex items-center rounded-md border border-input px-3">
                <span className="text-slate-500 text-sm mr-2">+91</span>
                <Input
                  data-testid="driver-phone-input"
                  placeholder="98765 43210"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="border-0 shadow-none focus-visible:ring-0 px-0"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">Vehicle Type</Label>
              <Select value={profile.vehicle_type} onValueChange={(v) => setProfile({ ...profile, vehicle_type: v })}>
                <SelectTrigger data-testid="driver-vehicle-select" className="mt-1.5">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicleTypes.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Operating Cities</Label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  data-testid="driver-city-input"
                  placeholder="e.g. Siliguri"
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const c = cityInput.trim();
                      if (c && !profile.cities.some((x) => x.toLowerCase() === c.toLowerCase()))
                        setProfile({ ...profile, cities: [...profile.cities, c] });
                      setCityInput("");
                    }
                  }}
                />
                <Button data-testid="driver-add-city-button" type="button" size="sm" variant="outline" className="rounded-lg shrink-0"
                  onClick={() => {
                    const c = cityInput.trim();
                    if (!c) return;
                    if (profile.cities.some((x) => x.toLowerCase() === c.toLowerCase())) return toast.error("City already added");
                    setProfile({ ...profile, cities: [...profile.cities, c] });
                    setCityInput("");
                  }}><Plus className="w-4 h-4" /></Button>
              </div>
              {profile.cities.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {profile.cities.map((c) => (
                    <span key={c} data-testid={`driver-city-chip-${c}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-xs font-medium text-amber-700">
                      {c}
                      <button data-testid={`driver-city-remove-${c}`} onClick={() => setProfile({ ...profile, cities: profile.cities.filter((x) => x !== c) })} className="text-amber-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-400 mt-1">You'll receive leads whose pickup is in any of these cities — add a city when you travel there for a ride.</p>
            </div>
            <Button
              data-testid="driver-save-profile-button"
              onClick={saveProfile}
              disabled={savingProfile}
              className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold"
            >
              {savingProfile ? "Saving..." : "Save Profile"}
            </Button>
          </div>
        </div>

        {/* Documents */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
          <h2 className="font-display font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-500" /> Verification Documents
          </h2>
          <p className="text-xs text-slate-500 mt-1">Upload both to complete registration. JPG, PNG or PDF (max 8MB).</p>
          <div className="mt-4 space-y-3">
            <DocRow
              label="Vehicle RC (Registration Certificate)"
              docType="rc"
              uploaded={user?.rc_filename}
              uploading={uploading.rc}
              inputRef={rcRef}
              onPick={(f) => uploadDoc("rc", f)}
            />
            <DocRow
              label="Driving Licence (DL)"
              docType="dl"
              uploaded={user?.dl_filename}
              uploading={uploading.dl}
              inputRef={dlRef}
              onPick={(f) => uploadDoc("dl", f)}
            />
          </div>
          {!regComplete && (
            <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-amber-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              Registration is incomplete until phone, vehicle type, RC and DL are all provided.
            </div>
          )}
          {regComplete && user?.driver_status === "pending" && (
            <div className="mt-4 flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-emerald-700 text-sm">
              <FileCheck2 className="w-4 h-4 shrink-0 mt-0.5" />
              Registration complete! Awaiting admin verification & approval.
            </div>
          )}
        </div>

        {/* Wallet */}
        {approved && (
          <div className="mt-4 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-2xl p-5 text-white shadow-lg" data-testid="driver-wallet-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-300 text-sm"><Wallet className="w-4 h-4 text-amber-400" /> Wallet Balance</div>
            </div>
            <p data-testid="driver-wallet-balance" className="font-display text-3xl font-extrabold mt-1">₹{Number(wallet).toLocaleString("en-IN")}</p>
            <div className="mt-4 flex gap-2">
              <div className="flex items-center flex-1 rounded-xl bg-white/10 px-3">
                <span className="text-slate-300 text-sm mr-1">₹</span>
                <Input data-testid="driver-topup-amount" type="number" value={topupAmt} onChange={(e) => setTopupAmt(e.target.value)}
                  className="border-0 bg-transparent text-white shadow-none focus-visible:ring-0 px-1 h-11 placeholder:text-slate-400" placeholder="Amount" />
              </div>
              <Button data-testid="driver-topup-button" onClick={topUp} disabled={topupBusy}
                className="h-11 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
                {topupBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Top Up</>}
              </Button>
            </div>
            <div className="mt-2 flex gap-2">
              {[500, 1000, 2000].map((a) => (
                <button key={a} onClick={() => setTopupAmt(String(a))} className="px-3 py-1 rounded-full bg-white/10 text-xs font-medium hover:bg-white/20">₹{a}</button>
              ))}
            </div>
          </div>
        )}

        {/* Active leads */}
        {approved && pushState === "default" && (
          <div data-testid="driver-push-banner" className="mt-6 bg-indigo-600 rounded-2xl p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-white shrink-0" />
              <p className="text-white text-sm font-medium">Get instant lead alerts even when the app is closed</p>
            </div>
            <Button data-testid="driver-push-enable-button" onClick={enablePush} size="sm" className="h-9 rounded-lg bg-white text-indigo-700 hover:bg-indigo-50 font-bold shrink-0">Enable Push</Button>
          </div>
        )}
        {approved && pushState === "unsupported" && (
          <div data-testid="driver-push-unsupported" className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 flex items-center gap-2">
            <Bell className="w-4 h-4 text-indigo-600 shrink-0" />
            <p className="text-sm font-medium text-indigo-700">For lead alerts with the app closed: on Android use Chrome, on iPhone install the app to your Home Screen first (Share → Add to Home Screen), then enable push here.</p>
          </div>
        )}
        {approved && pushState === "denied" && (
          <div data-testid="driver-push-denied" className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-600" />
            <p className="text-sm font-medium text-amber-700">Notifications blocked — enable them in your browser/app settings to get lead alerts</p>
          </div>
        )}
        {approved && pushState === "enabled" && (
          <div data-testid="driver-push-on" className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 flex items-center gap-2">
            <Bell className="w-4 h-4 text-emerald-600" />
            <p className="text-sm font-medium text-emerald-700">Push alerts on — you'll be notified of new leads</p>
          </div>
        )}
        {approved && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-lg text-slate-900">Active Leads</h2>
              <span className="text-xs text-slate-400">Live · auto-refresh</span>
            </div>
            {leads.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
                <Inbox className="w-10 h-10 text-slate-300 mx-auto" />
                <p className="mt-3 text-slate-500 font-medium">No active leads right now</p>
                <p className="text-xs text-slate-400">You'll hear a bell when a new lead is broadcast.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leads.map((l) => {
                  const mine = l.accepted_by === user.user_id;
                  return (
                    <div key={l.id} data-testid="driver-active-lead-card" onClick={stopRing} className={`bg-white rounded-2xl border p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)] ${l.premium ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-200/80"}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-1.5 font-display font-bold text-slate-900 text-lg">
                            {l.from_location} <ArrowRight className="w-4 h-4 text-amber-500" /> {l.to_location}
                          </div>
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" /> {formatDateTime(l.travel_date, l.travel_time)}
                            {l.distance_km ? ` · ~${l.distance_km} km` : ""}{l.passengers ? ` · ${l.passengers} pax` : ""}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                          {l.targeted && (
                            <Badge data-testid="driver-exclusive-badge" className="bg-emerald-600 text-white border border-emerald-600 font-bold">EXCLUSIVE</Badge>
                          )}
                          {l.premium && (
                            <Badge data-testid="driver-premium-badge" className="bg-amber-500 text-slate-900 border border-amber-500 font-bold">
                              <Star className="w-3 h-3 fill-slate-900 mr-1" /> PREMIUM
                            </Badge>
                          )}
                          <Badge className="bg-amber-50 text-amber-700 border border-amber-200">{l.vehicle_type}</Badge>
                        </div>
                      </div>
                      {!l.purchased && !mine ? (
                        <div className="mt-4 space-y-3">
                          <div data-testid="driver-lead-keyinfo" className="grid grid-cols-2 gap-2">
                            <div className="rounded-xl bg-slate-50 px-3 py-2">
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Date</p>
                              <p className="text-sm font-bold text-slate-900">{formatDateTime(l.travel_date, l.travel_time)}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 px-3 py-2">
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Vehicle</p>
                              <p className="text-sm font-bold text-slate-900">{l.vehicle_type}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 px-3 py-2">
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Passengers</p>
                              <p className="text-sm font-bold text-slate-900">{l.passengers ? `${l.passengers} pax` : "—"}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 px-3 py-2">
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Trip Type</p>
                              <p className="text-sm font-bold text-slate-900">{l.trip_type}</p>
                            </div>
                            {l.night_halts > 0 && (
                              <div data-testid="driver-night-halt" className="rounded-xl bg-slate-50 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Night Halt</p>
                                <p className="text-sm font-bold text-slate-900">{l.night_halts} night{l.night_halts > 1 ? "s" : ""}{l.night_halt_charge ? ` · ₹${Number(l.night_halt_charge).toLocaleString("en-IN")}` : ""}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
                            <span className="text-xs uppercase tracking-wider text-amber-700/80 font-medium">Lead Price</span>
                            <span data-testid="driver-lead-price" className="font-display text-2xl font-extrabold text-slate-900 flex items-center">
                              <IndianRupee className="w-5 h-5" />{Number(l.lead_price).toLocaleString("en-IN")}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 text-center">
                            {l.bids > 0 ? `${l.bids} driver(s) already bidding` : "Be the first to bid"} · Purchase to unlock fares & bid
                          </p>
                          <div data-testid="driver-lead-inclusions" className="flex flex-wrap gap-1.5">
                            {vehicleInclusions(l.vehicle_type).map((inc) => (
                              <span key={inc} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-xs font-medium text-emerald-700">
                                <Check className="w-3 h-3" /> {inc}
                              </span>
                            ))}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Button data-testid="driver-reject-lead-button" onClick={() => rejectLead(l.id)} disabled={rejecting === l.id}
                              variant="outline" className="h-12 rounded-xl border-red-200 text-red-600 hover:bg-red-50 font-bold">
                              {rejecting === l.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <><XCircle className="w-5 h-5 mr-1.5" /> Reject</>}
                            </Button>
                            <Button data-testid="driver-purchase-lead-button" onClick={() => purchaseLead(l.id)} disabled={purchasing === l.id}
                              className="h-12 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
                              {purchasing === l.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ShoppingCart className="w-5 h-5 mr-1.5" /> Purchase Lead</>}
                            </Button>
                          </div>
                        </div>
                      ) : (
                      <>
                      <div className="mt-4 space-y-2">
                        <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                          <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Admin Suggested Fare</span>
                          <span data-testid="driver-admin-fare" className="font-display text-2xl font-extrabold text-emerald-600 flex items-center">
                            {l.suggested_fare != null
                              ? (<><IndianRupee className="w-5 h-5" />{Number(l.suggested_fare).toLocaleString("en-IN")}</>)
                              : "—"}
                          </span>
                        </div>
                        {l.fare_offer != null && (
                          <div data-testid="driver-customer-offer" className="flex items-center justify-between rounded-xl bg-indigo-50 border border-indigo-100 px-4 py-2.5">
                            <span className="text-xs uppercase tracking-wider text-indigo-500 font-medium">Customer's Proposed Fare</span>
                            <span className="font-display font-bold text-indigo-700 flex items-center">
                              <IndianRupee className="w-4 h-4" />{Number(l.fare_offer).toLocaleString("en-IN")}
                            </span>
                          </div>
                        )}
                      </div>
                      {l.purchased && l.customer_phone && (
                        <div data-testid="driver-customer-contact" className={`mt-4 rounded-xl border px-4 py-3 ${mine ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                          <div className={`flex items-center gap-2 font-semibold ${mine ? "text-emerald-700" : "text-slate-700"}`}>
                            {mine ? <><CheckCircle2 className="w-5 h-5" /> Accepted by you</> : <><User className="w-5 h-5 text-slate-400" /> Customer Details</>}
                          </div>
                          <div className="mt-3 space-y-1.5 text-sm">
                            <p className="flex items-center gap-2 text-slate-700">
                              <User className="w-4 h-4 text-slate-400" /> {l.customer_name}
                            </p>
                            <p className="flex items-center gap-2 text-slate-900 font-semibold">
                              <PhoneCall className="w-4 h-4 text-emerald-600" /> +91 {l.customer_phone}
                            </p>
                          </div>
                          <a
                            data-testid="driver-call-customer"
                            href={`tel:+91${l.customer_phone}`}
                            className="mt-3 flex items-center justify-center gap-2 w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition-colors"
                          >
                            <PhoneCall className="w-4 h-4" /> Call Customer
                          </a>
                        </div>
                      )}
                      {l.purchased && (
                        <div className="mt-4 space-y-1.5">
                          <RouteMap from={l.from_location} to={l.to_location} stops={l.stopovers} testId="driver-route-map" />
                          {(l.stopovers || []).length > 0 && (
                            <p data-testid="driver-stopovers" className="text-xs text-slate-500"><span className="font-semibold text-slate-600">Stopovers:</span> {l.stopovers.join(" · ")}</p>
                          )}
                        </div>
                      )}
                      {l.purchased && !mine && !l.accepted_by && (
                        <div className="mt-4 grid grid-cols-2 gap-2">
                          <Button
                            data-testid="driver-reject-lead-button"
                            onClick={() => rejectLead(l.id)}
                            disabled={rejecting === l.id || accepting === l.id}
                            variant="outline"
                            className="h-12 rounded-xl border-red-200 text-red-600 hover:bg-red-50 font-bold"
                          >
                            {rejecting === l.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <><XCircle className="w-5 h-5 mr-1.5" /> Reject</>}
                          </Button>
                          <Button
                            data-testid="driver-accept-lead-button"
                            onClick={() => acceptLead(l.id)}
                            disabled={accepting === l.id || rejecting === l.id}
                            className="h-12 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                          >
                            {accepting === l.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-1.5" /> Accept</>}
                          </Button>
                        </div>
                      )}
                      </>
                      )}
                      {l.my_counter && (
                        <div
                          data-testid="driver-my-counter"
                          className={`mt-2 rounded-xl border px-4 py-2.5 text-sm font-medium flex items-center justify-between ${
                            l.my_counter.status === "pending"
                              ? "bg-amber-50 border-amber-200 text-amber-700"
                              : l.my_counter.status === "accepted"
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                              : "bg-red-50 border-red-200 text-red-600"
                          }`}
                        >
                          <span>Your counter: ₹{Number(l.my_counter.amount).toLocaleString("en-IN")}</span>
                          <span className="capitalize">{l.my_counter.status}</span>
                        </div>
                      )}
                      {l.purchased && !mine && !l.accepted_by && !l.my_counter && (
                        counterFor === l.id ? (
                          <div className="mt-2 flex gap-2">
                            <div className="flex items-center flex-1 rounded-xl border border-input px-3">
                              <IndianRupee className="w-4 h-4 text-slate-400" />
                              <Input
                                data-testid="driver-counter-amount-input"
                                type="number"
                                placeholder="Your fare"
                                value={counterAmount}
                                onChange={(e) => setCounterAmount(e.target.value)}
                                className="border-0 shadow-none focus-visible:ring-0 px-2 h-11"
                              />
                            </div>
                            <Button
                              data-testid="driver-counter-submit-button"
                              onClick={() => counterLead(l.id)}
                              disabled={counterBusy}
                              className="h-11 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
                            >
                              {counterBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            data-testid="driver-counter-open-button"
                            variant="outline"
                            onClick={() => { setCounterFor(l.id); setCounterAmount(""); }}
                            className="mt-2 w-full h-11 rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 font-semibold"
                          >
                            <Handshake className="w-4 h-4 mr-2" /> Counter Offer
                          </Button>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function DocRow({ label, docType, uploaded, uploading, inputRef, onPick }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800">{label}</p>
          {uploaded ? (
            <p className="text-xs text-emerald-600 flex items-center gap-1 mt-0.5 truncate">
              <FileCheck2 className="w-3.5 h-3.5" /> {uploaded}
            </p>
          ) : (
            <p className="text-xs text-slate-400 mt-0.5">Not uploaded yet</p>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.pdf"
          data-testid={`driver-${docType}-file-input`}
          className="hidden"
          onChange={(e) => { onPick(e.target.files[0]); e.target.value = ""; }}
        />
        <Button
          data-testid={`driver-upload-${docType}-button`}
          size="sm"
          variant={uploaded ? "outline" : "default"}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className={`h-9 rounded-lg shrink-0 ${uploaded ? "" : "bg-amber-500 hover:bg-amber-600 text-slate-900"}`}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> {uploaded ? "Replace" : "Upload"}</>}
        </Button>
      </div>
    </div>
  );
}

function StatusBanner({ status, regComplete }) {
  const map = {
    approved: {
      icon: CheckCircle2, cls: "bg-emerald-50 border-emerald-200 text-emerald-700",
      title: "You're Approved!", sub: "You can now receive and accept live leads.",
    },
    pending: {
      icon: Hourglass, cls: "bg-amber-50 border-amber-200 text-amber-700",
      title: "Approval Pending",
      sub: regComplete ? "Documents submitted. Admin is verifying your details." : "Complete your profile & upload RC + DL to get approved.",
    },
    rejected: {
      icon: ShieldX, cls: "bg-red-50 border-red-200 text-red-700",
      title: "Not Approved", sub: "Please contact support for assistance.",
    },
  };
  const s = map[status] || map.pending;
  return (
    <div data-testid="driver-approval-status-badge" className={`rounded-2xl border px-5 py-4 flex items-start gap-3 ${s.cls}`}>
      <s.icon className="w-6 h-6 shrink-0 mt-0.5" />
      <div>
        <p className="font-display font-bold">{s.title}</p>
        <p className="text-sm opacity-90">{s.sub}</p>
      </div>
    </div>
  );
}
